import React from 'react'
import { Button, Form, Select, Tooltip } from 'antd'

export type ChipTargetType = 'ids' | 'user_ids' | 'phones' | 'emails'

export function parseTargets(raw: string): string[] {
    return raw
        .split(/[\n,\t\r]+/)
        .map(v => v.trim())
        .filter(Boolean)
}

export function getEntryError(value: string, type: ChipTargetType): string {
    if (type === 'ids' || type === 'user_ids') {
        return /^\d+$/.test(value) ? '' : 'ערך לא תקין — צפוי מספר שלם'
    }
    if (type === 'phones') {
        if (value === '+') return ''
        if (/^\+/.test(value)) {
            const after = value.slice(1)
            if (after.length === 0) return ''
            if (/^0/.test(after)) return 'קוד מדינה לא יכול להתחיל ב-0'
            if (after.length < 7) return 'מספר בינלאומי קצר מדי'
            if (after.length > 15) return 'מספר בינלאומי ארוך מדי'
            return ''
        }
        if (/^0/.test(value)) {
            if (value.length < 10) return 'מספר קצר מדי — חייב להכיל 10 ספרות'
            if (!/^05\d{8}$/.test(value)) return 'מספר נייד ישראלי חייב להתחיל ב-05'
            return ''
        }
        return 'חייב להתחיל ב-05 או +'
    }
    if (type === 'emails') {
        if (/[֐-׿]/.test(value)) return 'כתובת אימייל לא יכולה להכיל אותיות בעברית'
        if (!value.includes('@')) return 'חסר @'
        const parts = value.split('@')
        if (parts.length !== 2) return 'יותר מ-@ אחד'
        const local = parts[0]
        const domain = parts[1]
        if (!local) return 'חסר שם לפני @'
        if (local.startsWith('.') || local.endsWith('.')) return 'שם לא יכול להתחיל או להסתיים בנקודה'
        if (/\.\./.test(value)) return 'אין אפשרות למייל עם שתי נקודות ברצף'
        if (!domain.includes('.')) return 'דומיין חייב להכיל נקודה'
        const tld = domain.split('.').pop() || ''
        if (tld.length < 2) return 'סיומת דומיין קצרה מדי'
        if (/\s/.test(value)) return 'אסור רווחים בכתובת'
        return ''
    }
    return ''
}

export function normalizeChipEntry(value: string, type: ChipTargetType): string {
    if (type === 'phones' && /^5\d{8}$/.test(value)) return '0' + value
    return value
}

export function sanitizeChipInput(value: string, type: ChipTargetType): string {
    if (type === 'ids' || type === 'user_ids') return value.replace(/\D+/g, '')
    if (type === 'phones') {
        const plus = value.startsWith('+')
        return plus ? '+' + value.replace(/\D+/g, '') : value.replace(/\D+/g, '')
    }
    if (type === 'emails') return value.replace(/[֐-׿]/g, '')
    return value
}

// --- Shared target-type switching logic ---

type SavedTargets<T extends string> = Partial<Record<T, string>>

export function makeTargetTypeHandler<T extends string>(
    form: any,
    getState: () => { targetType: T; savedTargets: SavedTargets<T> },
    setState: (updater: (s: any) => any) => void,
    extraFields?: Record<string, any>
) {
    return (newType: T) => {
        const { targetType, savedTargets } = getState()
        const currentTargets = form.getFieldValue('targets') || ''
        const updated = Object.assign({}, savedTargets, { [targetType]: currentTargets })
        const restored = updated[newType] || ''
        setState(() => ({ targetType: newType, savedTargets: updated, targetsInitial: restored }))
        form.setFieldsValue(Object.assign({ targets: restored }, extraFields || {}))
    }
}

// --- Shared target chip field (phones/emails/ids) with inline validation ---

interface TargetChipFieldProps {
    form: any
    targetType: ChipTargetType
    targetsInitial: string
    className?: string
}

export function TargetChipField({ form, targetType, targetsInitial, className }: TargetChipFieldProps) {
    const label = targetType === 'phones' ? 'טלפונים' : targetType === 'emails' ? 'אימיילים' : 'מזהי משתמש'
    const placeholder = 'ניתן להדביק ' + label + ' מופרדים בפסיק או שורה חדשה'

    const validateTargets = (_: any, value: string, callback: (msg?: string) => void) => {
        const items = parseTargets(value || '')
        if (items.length === 0) return callback()
        const bad = items.filter((v: string) => getEntryError(v, targetType) !== '').length
        if (bad === 1) return callback('ערך אחד לא תקין — רחף מעליו לפרטים')
        if (bad > 1) return callback(bad + ' ערכים לא תקינים — רחף עליהם לפרטים')
        callback()
    }

    return (
        <Form.Item label={label} style={{ marginBottom: 12 }}>
            {form.getFieldDecorator('targets', {
                initialValue: targetsInitial,
                rules: [{ validator: validateTargets }]
            })(<ChipInput targetType={targetType} placeholder={placeholder} className={className} />)}
        </Form.Item>
    )
}

interface ChipInputProps {
    value?: string
    onChange?: (val: string) => void
    targetType: ChipTargetType
    placeholder?: string
    className?: string
}

interface ChipInputState {
    inputValue: string
    editingIndex: number | null
    selected: Record<number, boolean>
    copied: boolean
}

export class ChipInput extends React.Component<ChipInputProps, ChipInputState> {
    state: ChipInputState = { inputValue: '', editingIndex: null, selected: {}, copied: false }
    inputRef: HTMLInputElement | null = null
    editInputRef: HTMLInputElement | null = null

    _onDocMouseDown = (e: MouseEvent) => {
        if (this.editInputRef && !this.editInputRef.contains(e.target as Node)) {
            this.commitEdit()
        }
    }

    componentDidUpdate(_: any, prev: ChipInputState) {
        if (prev.editingIndex === null && this.state.editingIndex !== null) {
            document.addEventListener('mousedown', this._onDocMouseDown)
        } else if (prev.editingIndex !== null && this.state.editingIndex === null) {
            document.removeEventListener('mousedown', this._onDocMouseDown)
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._onDocMouseDown)
    }

    chips(): string[] {
        return parseTargets(this.props.value || '')
    }

    setSanitized(raw: string) {
        this.setState({ inputValue: sanitizeChipInput(raw, this.props.targetType) })
    }

    commit() {
        const raw = this.state.inputValue.trim()
        if (!raw) return
        const newChips = parseTargets(raw).map(c => normalizeChipEntry(c, this.props.targetType))
        if (!newChips.length) {
            this.setState({ inputValue: '' })
            return
        }
        const existing = this.chips()
        const merged = existing.concat(newChips.filter(c => existing.indexOf(c) === -1))
        this.setState({ inputValue: '' })
        if (this.props.onChange) this.props.onChange(merged.join('\n'))
    }

    remove(index: number) {
        const chips = this.chips()
        chips.splice(index, 1)
        const selected: Record<number, boolean> = {}
        Object.keys(this.state.selected).forEach(key => {
            const i = Number(key)
            if (i < index) selected[i] = true
            else if (i > index) selected[i - 1] = true
        })
        this.setState({ selected })
        if (this.props.onChange) this.props.onChange(chips.join('\n'))
    }

    startEdit(index: number) {
        this.setState({ editingIndex: index, inputValue: this.chips()[index] }, () => {
            if (this.editInputRef) {
                this.editInputRef.focus()
            }
        })
    }

    commitEdit() {
        const { editingIndex } = this.state
        if (editingIndex === null) return
        const raw = this.state.inputValue.trim()
        const chips = this.chips()
        if (raw) {
            chips[editingIndex] = normalizeChipEntry(raw, this.props.targetType)
        } else {
            chips.splice(editingIndex, 1)
        }
        this.setState({ editingIndex: null, inputValue: '' })
        if (this.props.onChange) this.props.onChange(chips.join('\n'))
    }

    toggleSelect(index: number) {
        const selected = Object.assign({}, this.state.selected)
        if (selected[index]) {
            delete selected[index]
        } else {
            selected[index] = true
        }
        this.setState({ selected })
    }

    copyChips() {
        const chips = this.chips()
        const indices = Object.keys(this.state.selected).map(Number)
        const toCopy = indices.length > 0 ? indices.map(i => chips[i]).filter(Boolean) : chips
        navigator.clipboard.writeText(toCopy.join('\n'))
        this.setState({ copied: true }, () => setTimeout(() => this.setState({ copied: false }), 1500))
    }

    handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['Enter', ',', ' '].indexOf(e.key) !== -1) {
            e.preventDefault()
            this.commit()
            return
        }
        if (e.key === 'Backspace' && this.state.inputValue === '') {
            const chips = this.chips()
            if (chips.length > 0) this.remove(chips.length - 1)
        }
    }

    handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            this.commitEdit()
        }
        if (e.key === 'Escape') {
            document.removeEventListener('mousedown', this._onDocMouseDown)
            this.setState({ editingIndex: null, inputValue: '' })
        }
    }

    handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pasted = e.clipboardData.getData('text')
        const existing = this.chips()
        const newChips = parseTargets(sanitizeChipInput(this.state.inputValue + pasted, this.props.targetType))
            .map(c => normalizeChipEntry(c, this.props.targetType))
        const merged = existing.concat(newChips.filter(c => existing.indexOf(c) === -1))
        this.setState({ inputValue: '' })
        if (this.props.onChange) this.props.onChange(merged.join('\n'))
    }

    render() {
        const { targetType, placeholder, className } = this.props
        const { inputValue, editingIndex, selected, copied } = this.state
        const chips = this.chips()
        const selectedCount = Object.keys(selected).length

        return (
            <div>
                <div
                    className={className}
                    onClick={() => { if (this.inputRef) this.inputRef.focus() }}
                    style={{
                        minHeight: 80,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        padding: '6px 8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        alignItems: 'flex-start',
                        cursor: 'text',
                        background: '#fff',
                        direction: 'rtl'
                    }}
                >
                    {chips.map((chip, i) => {
                        const error = getEntryError(chip, targetType)
                        const isInvalid = !!error
                        const isSelected = !!selected[i]
                        const isEditing = editingIndex === i

                        if (isEditing) {
                            return (
                                <input
                                    key={i}
                                    ref={el => { this.editInputRef = el }}
                                    value={inputValue}
                                    onChange={e => this.setSanitized(e.target.value)}
                                    onKeyDown={this.handleEditKeyDown}
                                    style={{
                                        border: '1px solid #1890ff',
                                        borderRadius: 12,
                                        padding: '2px 8px',
                                        fontSize: 13,
                                        outline: 'none',
                                        minWidth: 80,
                                        direction: 'ltr'
                                    }}
                                />
                            )
                        }

                        const bg = isSelected ? '#e6f7ff' : isInvalid ? '#fff1f0' : '#f6ffed'
                        const borderColor = isSelected ? '#1890ff' : isInvalid ? '#ff4d4f' : '#b7eb8f'
                        const color = isInvalid ? '#ff4d4f' : '#389e0d'

                        const chipEl = (
                            <span
                                key={i}
                                onClick={e => { e.stopPropagation(); this.toggleSelect(i) }}
                                onDoubleClick={e => { e.stopPropagation(); this.startEdit(i) }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '2px 8px',
                                    borderRadius: 12,
                                    fontSize: 13,
                                    lineHeight: '20px',
                                    background: bg,
                                    border: '1px solid ' + borderColor,
                                    color,
                                    maxWidth: 240,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    direction: 'ltr',
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}
                            >
                                {chip}
                                <span
                                    onClick={e => { e.stopPropagation(); this.remove(i) }}
                                    style={{ cursor: 'pointer', fontSize: 12, opacity: 0.5, lineHeight: 1, paddingLeft: 7 }}
                                >
                                    ×
                                </span>
                            </span>
                        )

                        return isInvalid ? (
                            <Tooltip key={i} title={error} placement="top">{chipEl}</Tooltip>
                        ) : chipEl
                    })}
                    <input
                        ref={el => { this.inputRef = el }}
                        value={editingIndex !== null ? '' : inputValue}
                        onChange={e => this.setSanitized(e.target.value)}
                        onKeyDown={this.handleKeyDown}
                        onBlur={() => { if (this.state.editingIndex === null) this.commit() }}
                        onPaste={this.handlePaste}
                        placeholder={chips.length === 0 ? placeholder : ''}
                        style={{
                            border: 'none',
                            outline: 'none',
                            flex: 1,
                            minWidth: 120,
                            fontSize: 14,
                            background: 'transparent',
                            direction: inputValue ? 'ltr' : 'rtl',
                            textAlign: inputValue ? 'left' : 'right'
                        }}
                    />
                </div>
                {chips.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', direction: 'rtl' }}>
                        <Button
                            size="small"
                            icon={copied ? 'check' : 'copy'}
                            onClick={() => this.copyChips()}
                            style={{ fontSize: 12 }}
                        >
                            {copied
                                ? 'הועתק!'
                                : selectedCount > 0
                                ? 'העתק נבחרים (' + selectedCount + ')'
                                : 'העתק הכל (' + chips.length + ')'}
                        </Button>
                        {selectedCount > 0 && (
                            <span
                                onClick={() => this.setState({ selected: {} })}
                                style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}
                            >
                                בטל בחירה
                            </span>
                        )}
                        <span style={{ fontSize: 11, color: '#bbb', marginRight: 'auto' }}>
                            לחץ לבחירה · לחץ פעמיים לעריכה
                        </span>
                    </div>
                )}
            </div>
        )
    }
}
